import { PLATFORM, STORYBOOK_PLATFORM } from "@shared/env";

export type Platform = "WEB" | "DESKTOP" | "MOBILE";

/**
 * Checks if running inside the Electron desktop app.
 * Detects the ElectronView API exposed by the desktop app's preload script.
 */
function isElectronApp(): boolean {
  return !!(typeof window !== "undefined" && window.ElectronView?.isElectron);
}

/**
 * Checks for unique runtime environments that override the build-time PLATFORM.
 * Currently detects Electron desktop app.
 */
function checkForUniqueEnvironments(): Platform | null {
  if (isElectronApp()) {
    return "DESKTOP";
  }
  return null;
}

/**
 * @returns the platform the app is running on
 * Priority: Runtime detection > Build-time env > Storybook > Default
 */
export function getPlatform(): Platform {
  // Runtime environment detection takes priority
  const runtimePlatform = checkForUniqueEnvironments();
  if (runtimePlatform) {
    return runtimePlatform;
  }

  return (PLATFORM ?? STORYBOOK_PLATFORM ?? "WEB") as Platform;
}

/**
 * Check if the current platform matches the specified platform.
 * This is the preferred way to check platform throughout the codebase.
 *
 * @example
 * if (isPlatform("desktop")) { ... }
 * if (isPlatform("web")) { ... }
 * if (isPlatform("mobile")) { ... }
 */
export function isPlatform(platform: Lowercase<Platform>): boolean {
  return getPlatform().toLowerCase() === platform;
}
